import { useEffect, useRef } from 'react';

import { useApi } from 'app/shared/contexts/api';
import { useUser } from 'app/shared/contexts/user';

import { resolveSearchUrl } from './use-activity-feed-query.hook';
import { useSyncActivityFeed } from './use-sync-activity-feed.hook';

/*
 * search-service pushes a Server-Sent-Events frame per activity change for the current user, so the
 * browser holds ONE open connection instead of the feed re-requesting on every focus/mount. A frame
 * carries the raw domain event, NOT the locale-resolved, server-sorted `activity_events` projection the
 * feed renders — so a frame is treated purely as a SIGNAL TO RE-READ the authoritative source, never
 * merged from the frame body, which would re-introduce the client-side bucketing and locale drift the
 * unified projection deliberately removed.
 *
 * The re-read is a silent in-place patch, NOT an invalidation — see `useSyncActivityFeed`. A push must
 * not move the list or raise a spinner under someone who did not ask for either.
 */
/*
 * `includeAllEventStatuses` opts this stream in to event frames of EVERY status.
 *
 * It defaults to false server-side because both SSE endpoints are shared with users-admin, which
 * cannot yet label waitlisted or cancelled registrations. This feed's READ path has no such filter,
 * so without opting in the stream would be strictly behind the api it invalidates: cancelling a
 * registration would push nothing and the tile would only correct on the next refetch.
 */
const activityStreamUrl = (searchUrl: string, userUuid: string): string =>
  `${searchUrl}/search/v1/activity-events/events/${userUuid}?includeAllEventStatuses=true`;

// The stream interleaves `event: heartbeat` frames (empty data) every 25s so the load balancer doesn't
// drop the idle connection. They carry no change and must not trigger a refetch.
const HEARTBEAT_EVENT = 'heartbeat';

// Coalesce a burst of frames (e.g. a bulk change) into a single sync.
const REFETCH_DEBOUNCE_MS = 400;

// Reconnect backoff: a dropped stream retries, growing to a cap so a persistent outage doesn't hammer.
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * Half the delay fixed, half random.
 *
 * A search-service deploy drops every client's stream at the same instant, so an exact backoff has
 * them all return together — repeatedly, and hardest at the moment the new pods are coldest. The
 * random half spreads that herd out; the fixed half keeps the growth meaningful.
 */
const withJitter = (delay: number): number => delay / 2 + Math.random() * (delay / 2);

/**
 * How long silence is allowed before the connection is presumed dead.
 *
 * A dropped TCP connection does not always arrive as an error — a sleeping laptop, a network
 * change, a load balancer reaping an idle socket all leave a read that simply never resolves. The
 * hook would sit on it forever, connected in name only: no frames, no error, no reconnect.
 *
 * The server heartbeats every 25s (`HEARTBEAT_INTERVAL_MS` in search-service), so this is a
 * multiple of that, not an absolute duration. THREE missed beats plus slack, deliberately, because
 * the cost of the two mistakes is wildly asymmetric:
 *
 * - Too slow, and a genuinely dead pipe is noticed a few seconds later than it could have been.
 * - Too fast, and a HEALTHY connection is killed. That one compounds: `reconnectAttempt` only
 *   resets when bytes arrive, and the watchdog fires precisely when they have not, so each
 *   spurious kill reconnects on a longer backoff than the last — 1s, 2s, 4s, out to the 30s cap.
 *   Every change that lands inside those widening gaps is simply never seen, which reads to a user
 *   as the stream working intermittently, or working for some activity types and not others.
 *
 * A single delayed beat — a GC pause, a throttled timer in a backgrounded webview, a slow pod —
 * must therefore never be enough to trip this.
 */
export const STREAM_IDLE_TIMEOUT_MS = 90_000;

/*
 * SSE permits CRLF, CR or LF as a line ending — independently — so a blank-line frame boundary can be
 * any two consecutive endings, INCLUDING mixed pairs like `\n\r\n`. Matching only `\n\n` leaves a CRLF
 * frame sitting in the buffer forever: the refetch never fires and the feed silently stops updating,
 * with a healthy-looking connection still open.
 *
 * Every ending is normalized to `\n` as bytes arrive, so a boundary is always `\n\n`, rather than
 * enumerating the pairs in a separator regex — a `(?:\r\n|\r|\n){2}` alternation backtracks and
 * splits a lone CRLF, and spelling the pairs out still mis-slices a boundary whose first half is `\r`.
 *
 * A lone trailing `\r` is carried into the next read rather than normalized now: it may be the front
 * half of a CRLF split across two network chunks, and collapsing it early would fake a boundary
 * against the next chunk's `\n`.
 */
const FRAME_LINE = /\r\n|\r|\n/;
const CARRIAGE_RETURN = '\r';
const normalizeLineEndings = (text: string): string => text.replace(/\r\n|\r/g, '\n');

const isHeartbeatFrame = (frame: string): boolean =>
  frame
    .split(FRAME_LINE)
    .some((line) => line.startsWith('event:') && line.slice('event:'.length).trim() === HEARTBEAT_EVENT);

/**
 * Hold one SSE connection to search-service for the current user and refetch the activity feed whenever
 * a real change lands — replacing the feed's focus/mount re-request polling with a push.
 *
 * Fetch-based rather than `EventSource` because the read needs the app's `Authorization` header, which
 * `EventSource` cannot set; the gateway accepts the same headers the feed already uses. Mount-once in the
 * feed surface — it has no return value, driving the shared feed cache through invalidation.
 */
export const useActivityFeedStream = (): void => {
  const { user } = useUser();
  const { apiUrl, getAuthHeaders } = useApi();
  const syncFeed = useSyncActivityFeed();

  const userUuid = user?.uuid;
  const searchUrl = apiUrl ? resolveSearchUrl(apiUrl) : '';

  /*
   * Latest values held in refs so the connection effect depends only on identity (user/url), not on the
   * fresh function references React hands us each render — otherwise it would tear down and reconnect on
   * every parent re-render. Reading the auth getter through a ref also means a reconnect after a token
   * refresh picks up the new value.
   *
   * Filled in an EFFECT rather than during render. A ref written mid-render is not undone when React
   * discards that render — and it discards them routinely under StrictMode and concurrent rendering —
   * so the connection could go on to reconnect using a value from a render that never committed.
   * Writing after commit means the ref only ever holds something the tree actually adopted.
   *
   * Declared above the connection effect on purpose: effects run in declaration order, so both refs
   * hold the committed value before the first connection opens.
   */
  const syncRef = useRef(syncFeed);
  useEffect(() => {
    syncRef.current = syncFeed;
  }, [syncFeed]);

  const getAuthHeadersRef = useRef(getAuthHeaders);
  useEffect(() => {
    getAuthHeadersRef.current = getAuthHeaders;
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!userUuid || !searchUrl) return undefined;

    /*
     * Torn-down state and a handle on the connection currently in flight, rather than one shared
     * AbortController. The idle watchdog has to abandon a SINGLE dead connection without disarming
     * teardown — and a per-attempt controller chained to an outer one by event listener is more
     * lifecycle than this needs.
     */
    let isTornDown = false;
    let currentAttempt: AbortController | undefined;
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let reconnectAttempt = 0;

    const scheduleSync = (): void => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        syncRef.current();
      }, REFETCH_DEBOUNCE_MS);
    };

    const readStream = async (
      body: ReadableStream<Uint8Array>,
      onBytes: () => void,
    ): Promise<void> => {
      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // A lone `\r` at the end of a chunk may be the front half of a CRLF still in flight — hold it.
      let heldCarriageReturn = false;
      let streamDone = false;
      while (!streamDone) {
        // Sequential by nature — each SSE frame is read off the socket in order.
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        streamDone = done;
        if (value) {
          // ANY bytes prove the pipe is alive, heartbeats included — that is what they are for.
          onBytes();
          const decoded: string = (heldCarriageReturn ? CARRIAGE_RETURN : '') + decoder.decode(value, { stream: true });
          heldCarriageReturn = decoded.endsWith(CARRIAGE_RETURN);
          buffer += normalizeLineEndings(heldCarriageReturn ? decoded.slice(0, -1) : decoded);
          let boundary = buffer.indexOf('\n\n');
          while (boundary !== -1) {
            const frame = buffer.slice(0, boundary);
            if (frame && !isHeartbeatFrame(frame)) scheduleSync();
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf('\n\n');
          }
        }
      }
    };

    const connect = async (): Promise<void> => {
      // One controller per attempt, so the watchdog can drop this connection and no other.
      const attempt = new AbortController();
      currentAttempt = attempt;

      let idleTimer: ReturnType<typeof setTimeout> | undefined;
      const armIdleWatchdog = (): void => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => attempt.abort(), STREAM_IDLE_TIMEOUT_MS);
      };

      /*
       * Armed BEFORE the awaits below, not after the handshake returns. Neither `getAuthHeaders()`
       * nor `fetch` has a timeout of its own, so a token read or a connect that never settles — a
       * laptop that slept mid-handshake, a network change — would otherwise hang here with no
       * watchdog running at all: no frames, no error, and nothing to notice it.
       */
      armIdleWatchdog();

      try {
        const headers = await getAuthHeadersRef.current();
        const response = await fetch(activityStreamUrl(searchUrl, userUuid), {
          method: 'GET',
          headers: { ...headers, Accept: 'text/event-stream' },
          signal: attempt.signal,
        });
        if (!response.ok || !response.body) throw new Error(`activity stream responded ${response.status}`);

        // A successful RECONNECT (not the first connect) may have missed changes while it was down —
        // catch up once. The first connect is skipped: the feed's own initial fetch already holds fresh
        // data. Still the silent patch: returning to a list that reorders itself is the jolt this
        // avoids, and anything genuinely new arrives with the next natural refresh.
        if (reconnectAttempt > 0) syncRef.current();
        armIdleWatchdog();

        await readStream(response.body, () => {
          /*
           * The backoff resets HERE, on bytes actually arriving — not on the handshake returning.
           *
           * A gateway that accepts the request and then closes it (istio or a load balancer that
           * has not been told about `text/event-stream` does exactly this) answers 200 every time.
           * Resetting on that produced a connection whose backoff never grew: a reconnect every
           * second, and with it a full feed refetch every second, from every open client — a load
           * spike aimed at the service that was already unwell.
           */
          reconnectAttempt = 0;
          armIdleWatchdog();
        });
        // A closed stream is not the steady state — fall through to reconnect below.
        throw new Error('activity stream closed');
      } catch {
        if (isTornDown) return;
        const delay = withJitter(Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempt, RECONNECT_MAX_MS));
        reconnectAttempt += 1;
        reconnectTimer = setTimeout(() => {
          connect().catch(() => undefined);
        }, delay);
      } finally {
        if (idleTimer) clearTimeout(idleTimer);
      }
    };

    connect().catch(() => undefined);

    return () => {
      isTornDown = true;
      currentAttempt?.abort();
      if (debounceTimer) clearTimeout(debounceTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [userUuid, searchUrl]);
};
